import React, {useEffect, useState} from "react";
import {Button, EmptyState, Field, Input, SectionHeading, Switch} from "./ui";

const ChooseSolutionV2 = ({
                              solutions,
                              setTotalCredits,
                              selected,
                              setSelected,
                              setSelectedOptions,
                              selectedOptions,
                              setSolutionErrorMessage
                          }) => {
    const [dtcValue, setDtcValue] = useState("");
    const [showDtcOffInput, setShowDtcOffInput] = useState(false);

    useEffect(() => {
        const total = calculateTotalCredits(
            selected.TUNING,
            selected.OPTION
        );

        setTotalCredits(total);
    }, [selected, solutions, setTotalCredits]);

    const handleRadioToggle = (solution) => {
        setSelected((prev) => {
            // if the same tuning is clicked again → deselect
            const newTuning = prev.TUNING === solution.id ? null : solution.id;

            return {...prev, TUNING: newTuning};
        });

        setSelectedOptions((prevSelectedOptions) => {
            const isAlreadySelected = prevSelectedOptions.includes(solution.name);

            // if deselecting the same tuning → remove it
            if (selected.TUNING === solution.id && isAlreadySelected) {
                return prevSelectedOptions.filter((name) => name !== solution.name);
            }

            // remove all other tuning options before adding the new one
            const cleaned = prevSelectedOptions.filter(
                (name) => !solutions.some((sol) => sol.type === "TUNING" && sol.name === name)
            );

            return [...cleaned, solution.name];
        });

        // recalc credits
        const newTuningId = selected.TUNING === solution.id ? null : solution.id;
        calculateTotalCredits(newTuningId, selected.OPTION);
        setSolutionErrorMessage("");
    };

    const handleCheckboxToggle = (solution) => {
        const isSelected = selected.OPTION.includes(solution.id);
        const updatedOptions = isSelected
            ? selected.OPTION.filter((id) => id !== solution.id)
            : [...selected.OPTION, solution.id];

        if (solution.name === "DTC OFF") {
            const isSelected = selectedOptions.includes("DTC OFF");
            if (!isSelected) {
                setShowDtcOffInput(true);
            } else {
                setSelectedOptions((prev) =>
                    prev.filter((option) => !option.startsWith("DTC:"))
                );
                setShowDtcOffInput(false);
            }
        }

        setSelected((prev) => ({...prev, OPTION: updatedOptions}));

        setSelectedOptions((prevSelectedOptions) => {
            if (prevSelectedOptions.includes(solution.name)) {
                return prevSelectedOptions.filter((name) => name !== solution.name);
            } else {
                return [...prevSelectedOptions, solution.name];
            }
        });

        calculateTotalCredits(selected.TUNING, updatedOptions);
        setSolutionErrorMessage("");
    };

    const calculateTotalCredits = (tuningId, optionIds) => {
        const tuningCredits = tuningId
            ? solutions.find((s) => s.id === tuningId)?.credits || 0
            : 0;

        const optionsCredits = optionIds.reduce((acc, id) => {
            const sol = solutions.find((s) => s.id === id);
            return acc + (sol?.credits || 0);
        }, 0);

        return tuningCredits + optionsCredits;
    };

    const handleDtcValue = () => {
        setSelectedOptions((prev) => [...prev, `DTC:${dtcValue}`]);
    };

    const resetForm = () => {
        setSelected({TUNING: null, OPTION: []});
        setSelectedOptions([]);
        setTotalCredits(0);
        setDtcValue("");
        setShowDtcOffInput(false);
    };

    // A solution tile: artwork, name and price, and the switch that selects it.
    // Selected tiles carry the brand tint so a chosen set reads at a glance.
    const renderCard = (solution, isActive, toggleFn) => (
        <div
            key={solution.id}
            onClick={() => toggleFn(solution)}
            className={`flex cursor-pointer items-center gap-3 rounded-[16px] border p-3 transition ${
                isActive
                    ? "border-red-500/50 bg-gradient-to-br from-red-500/10 to-orange-500/10"
                    : "border-gray-200 bg-white hover:border-red-300 dark:border-[#3a3a3d]/70 dark:bg-[#262628] dark:hover:border-orange-400/40"
            }`}
        >
            <img
                src={solution.image_url}
                alt={solution.name}
                className="h-14 w-14 shrink-0 rounded-[12px] border border-gray-200 object-cover dark:border-[#3a3a3d]"
            />
            <div className="min-w-0 flex-1">
                <p
                    className={`truncate text-sm font-semibold ${
                        isActive ? "text-red-700 dark:text-orange-300" : "text-gray-800 dark:text-slate-100"
                    }`}
                    title={solution.name}
                >
                    {solution.name}
                </p>
                <span
                    className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${
                        isActive
                            ? "bg-red-500/15 text-red-600 dark:text-orange-300"
                            : "bg-gray-100 text-gray-500 dark:bg-[#303032] dark:text-slate-300"
                    }`}
                >
                    {solution.credits} CRS
                </span>
            </div>
            <Switch checked={isActive} onToggle={() => toggleFn(solution)} label={solution.name} />
        </div>
    );

    return (
        <div className="mt-6 space-y-6">
            {/* Tuning Options */}
            <div>
                <SectionHeading title="Tuning Options"/>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {solutions.filter((s) => s.type === "TUNING").length === 0 ? (
                        <EmptyState className="sm:col-span-2 xl:col-span-3" title="No tuning options" />
                    ) : (
                        solutions
                            .filter((s) => s.type === "TUNING")
                            .map((solution) =>
                                renderCard(
                                    solution,
                                    selected.TUNING === solution.id,
                                    handleRadioToggle
                                )
                            )
                    )}
                </div>
            </div>

            {/* Additional Options */}
            <div>
                <SectionHeading title="Additional Options"/>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {solutions.filter((s) => s.type === "OPTION").length === 0 ? (
                        <EmptyState className="sm:col-span-2 xl:col-span-3" title="No additional options" />
                    ) : (
                        solutions
                            .filter((s) => s.type === "OPTION")
                            .map((solution) =>
                                renderCard(
                                    solution,
                                    selected.OPTION.includes(solution.id),
                                    handleCheckboxToggle
                                )
                            )
                    )}
                </div>
            </div>

            {/* DTC Input */}
            {showDtcOffInput && (
                <div className="w-full max-w-md rounded-[16px] border border-gray-200 bg-gray-50 p-4 dark:border-[#2c2c2e] dark:bg-[#161617]">
                    <Field label="Write DTC" hint="Enter DTC codes (P, U) etc.">
                        <Input
                            type="text"
                            value={dtcValue}
                            onChange={(e) => setDtcValue(e.target.value)}
                            placeholder="Enter DTC (P, U) etc…"
                        />
                    </Field>
                    <div className="mt-3 flex justify-end">
                        <Button
                            size="sm"
                            onClick={() => {
                                handleDtcValue();
                                setShowDtcOffInput(false);
                            }}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="flex justify-end">
                <Button type="button" variant="subtle" size="sm" onClick={resetForm}>
                    Reset
                </Button>
            </div>
        </div>
    );
};

export default ChooseSolutionV2;